/**
 * Letter Zoom Animation Component
 *
 * Camera zoom-out paradigm animation variant for DramaDetector.
 * O(1) performance via single scaled element with chromatic layers.
 *
 * Phase timing (LETTER_ZOOM_PHASE_TIMING):
 * - Emergence (0-55%): Dynamic zoom from letter → 3 lines
 * - Transition (55-60%): Bridge crossfade to reading
 * - Reading (60-82%): Saccadic scroll (reduced from 85%)
 * - Convergence (82-90%): Shrink, center, clear text
 * - Revelation (90-100%): Text dims to backdrop, badge reveals with delay
 *
 * Extracted from LetterThrowingAnimation.web.tsx for <300L compliance.
 */

import React, { useEffect, useState, useMemo, useCallback, useRef, memo } from 'react';
import { View, Text, Pressable, Animated, Platform, Easing, ActivityIndicator } from 'react-native';
import { LinearGradient } from 'expo-linear-gradient';
import { useTranslation } from '@dramadetector/shared';
import { formatDramaScore } from '@dramadetector/ui';
import type { LetterThrowingAnimationProps, AnimationPhase } from '../types';
import {
  Y2K_GRADIENT_COLORS,
  FOCUS_BLUR_LEVELS,
  LETTER_ZOOM_PHASE_TIMING,
  LETTER_ZOOM_PHASE_BOUNDARIES,
  CONVERGENCE_CONSTANTS,
  REVELATION_CONSTANTS,
  NARRATOR_SUBTITLE_CONSTANTS,
  generateDynamicZoomSteps,
  calculateDynamicStepBoundaries,
  calculateReadingWindow,
  calculateAnimationDuration,
} from '../types';
import { getPatternQuip } from '../../../lib/executiveProducerQuips';
import { useSpeedPresets } from '../hooks/useSpeedPresets';
import { splitIntoLines } from './sharedAnimationUtils';
import { ANIMATION_BG_GRADIENT, READING_LINE_HEIGHT, READING_PADDING_LINES } from '../animationHelpers.web';
import { getCategoryFromScore, getCategoryStyle } from '../../../lib/categoryStyles';
import { getZoomGlowColor } from './letterZoomUtils';
import { styles } from './letterZoomStyles';
import { THEATRICAL_MODE_CONFIGS } from '../../../lib/theatrical/theatricalModeConfig';
// captureNarratorStreamingStarted/captureNarratorDisplayComplete removed with dead LLM narrator code
import { NARRATOR_EXPERIENCE_ENABLED } from '../narratorFeatureFlags';

/** Reading phase constants */
const LINE_HEIGHT = READING_LINE_HEIGHT;
const PADDING_LINES = READING_PADDING_LINES;

// LLM_NARRATOR_INTRO_ENABLED was permanently false (dead code) — removed.
// The useEffect at line ~141 that depended on it now returns early unconditionally.
// Loading quips still work independently via NARRATOR_EXPERIENCE_ENABLED.

/**
 * Letter Zoom Experience Animation
 * Main component for letterZoom variant
 */
const LetterZoomAnimation = memo(function LetterZoomAnimation({
  text,
  speed = '3',
  onComplete,
  onSkip: _onSkip,
  style,
  analysisResult,
  theatricalConfig,
}: LetterThrowingAnimationProps) {
  const { t } = useTranslation('home');
  const { t: tContent } = useTranslation('content'); // For narrator quips
  const { settings } = useSpeedPresets(speed);

  // Use provided theatrical config or default to quick mode
  const effectiveConfig = theatricalConfig || THEATRICAL_MODE_CONFIGS.quick;

  // Parse words from text for semantic zoom
  const words = useMemo(() => text.split(/\s+/), [text]);

  // Dynamic zoom steps based on word count (10% coverage)
  const dynamicZoomSteps = useMemo(() => {
    return generateDynamicZoomSteps(words.length, 0.10);
  }, [words.length]);

  // Pre-calculate step boundaries for O(n) lookup
  const dynamicStepBoundaries = useMemo(() => {
    return calculateDynamicStepBoundaries(dynamicZoomSteps);
  }, [dynamicZoomSteps]);

  // Target word count from last step (for 40-50% coverage)
  const targetWordCount = useMemo(() => {
    return dynamicZoomSteps[dynamicZoomSteps.length - 1]?.wordCount || 35;
  }, [dynamicZoomSteps]);

  // State
  const [progress, setProgress] = useState(0);
  const [phase, setPhase] = useState<AnimationPhase>('idle');
  const [showSkipHint, setShowSkipHint] = useState(false);
  const [showContinueButton, setShowContinueButton] = useState(false);

  // SUSPENSE PHASE: State for theatrical mode
  const [isInSuspense, setIsInSuspense] = useState(false);
  const [suspenseMessageIndex, setSuspenseMessageIndex] = useState(0);
  const [suspenseScale, setSuspenseScale] = useState(1); // Breathing animation scale
  const SUSPENSE_MESSAGES = effectiveConfig.suspense.messages;

  // Narrator subtitle state (reality TV commentary)
  // Shows during emergence phase with fallback quips, OR when LLM narrator arrives
  const [narratorQuip, setNarratorQuip] = useState<string>('');
  const [showNarratorSubtitle, setShowNarratorSubtitle] = useState(false);
  const narratorSubtitleShownRef = useRef(false);
  const llmNarratorArrivedRef = useRef(false);
  const llmNarratorArrivalTimeRef = useRef<number>(0);

  // Display duration for LLM narrator after it arrives (same as quip display)
  const LLM_NARRATOR_DISPLAY_DURATION_MS = NARRATOR_SUBTITLE_CONSTANTS.DISPLAY_DURATION_MS * 2; // 2.8s

  // LLM narrator intro feature was permanently disabled (dead code removed).
  // Loading quips still work independently via NARRATOR_EXPERIENCE_ENABLED.

  // Zoom emergence state
  const [currentZoomText, setCurrentZoomText] = useState('');
  const [currentScale, setCurrentScale] = useState(8);
  const [currentGlowColor, setCurrentGlowColor] = useState<string>(Y2K_GRADIENT_COLORS.pink);

  // Reading state
  const [readingLines, setReadingLines] = useState<string[]>([]);
  const [currentLineIndex, setCurrentLineIndex] = useState(0);
  const [readingScrollY, setReadingScrollY] = useState(0);
  const [convergenceScale, setConvergenceScale] = useState(1); // Shrinks from 1.0 → 0.65 during convergence
  const [convergenceBlurProgress, setConvergenceBlurProgress] = useState(0); // 0→1 during convergence (blur reduces to 0)

  // Badge animation
  const [badgeScale] = useState(new Animated.Value(0));
  const [badgeOpacity] = useState(new Animated.Value(0));
  const [textOpacity] = useState(new Animated.Value(1));
  const [zoomTextOpacity] = useState(new Animated.Value(1));

  // Y2K Animated border state
  // Note: Animated.loop() breaks on React Native Web - using recursive animation instead
  const [borderRotation] = useState(new Animated.Value(0));
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);
  const borderAnimationRef = useRef<Animated.CompositeAnimation | null>(null);
  const isAnimatingRef = useRef(false);

  // Refs
  const rafIdRef = useRef<number | null>(null);
  const startTimeRef = useRef<number>(0);
  const skipRequestedRef = useRef(false);
  const phaseRef = useRef<AnimationPhase>('idle');
  const readingLinesRef = useRef<string[]>([]);
  const analysisResultRef = useRef(analysisResult);
  const suspenseStartedRef = useRef(false); // Track if suspense phase has been triggered
  const suspenseStartTimeRef = useRef<number>(0); // When suspense phase started

  // Phase boundaries (pre-calculated)
  const { convergenceEnd } = LETTER_ZOOM_PHASE_BOUNDARIES;

  // Category styling
  const category = analysisResult?.category || getCategoryFromScore(analysisResult?.score || 50);
  const categoryStyle = getCategoryStyle(category);

  // Duration based on speed with minimum floor for LLM latency buffer
  // @see ANIMATION_DURATION_CONSTANTS in types.ts for Theatrical Time Architecture
  const totalDuration = useMemo(() => {
    return calculateAnimationDuration(settings.multiplier);
  }, [settings.multiplier]);

  // Initialize reading lines with padding
  useEffect(() => {
    const lines = splitIntoLines(text, 50);
    const paddingDots = Array(PADDING_LINES).fill('· · ·');
    const paddedLines = [...paddingDots, ...lines, ...paddingDots];
    setReadingLines(paddedLines);
    readingLinesRef.current = paddedLines;
  }, [text]);

  // Initialize zoom text
  useEffect(() => {
    if (words.length > 0) {
      setCurrentZoomText(words[0].charAt(0));
    }
  }, [words]);

  // Keep analysisResultRef fresh and show skip hint when API responds
  useEffect(() => {
    analysisResultRef.current = analysisResult;

    // Show skip hint only after API response is received (badge can be revealed)
    // This ensures users can only skip when there's actually a result to show
    if (analysisResult && !showContinueButton) {
      const hintDelay = setTimeout(() => setShowSkipHint(true), 500);
      return () => clearTimeout(hintDelay);
    }
  }, [analysisResult, showContinueButton]);

  // Detect prefers-reduced-motion for border animation (WCAG 2.1 compliance)
  useEffect(() => {
    if (Platform.OS !== 'web') return;

    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    setPrefersReducedMotion(mediaQuery.matches);

    const handler = (e: MediaQueryListEvent) => setPrefersReducedMotion(e.matches);
    mediaQuery.addEventListener('change', handler);
    return () => mediaQuery.removeEventListener('change', handler);
  }, []);

  // Y2K Traveling spark - continuous rotation (recursive loop, not Animated.loop which breaks on web)
  useEffect(() => {
    if (!showContinueButton || prefersReducedMotion || Platform.OS !== 'web' || isAnimatingRef.current) {
      return;
    }

    isAnimatingRef.current = true;
    borderRotation.setValue(0);

    const startRotation = () => {
      borderAnimationRef.current = Animated.timing(borderRotation, {
        toValue: 1,
        duration: 2000,
        easing: Easing.linear,
        useNativeDriver: false,
      });

      borderAnimationRef.current.start(({ finished }) => {
        if (finished && isAnimatingRef.current) {
          borderRotation.setValue(0);
          startRotation();
        }
      });
    };

    startRotation();

    return () => {
      isAnimatingRef.current = false;
      borderAnimationRef.current?.stop();
      borderRotation.setValue(0);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- borderRotation is stable Animated.Value
  }, [showContinueButton, prefersReducedMotion]);

  // Border rotation interpolation (0 → 360deg continuous)
  const borderRotate = borderRotation.interpolate({
    inputRange: [0, 1],
    outputRange: ['0deg', '360deg'],
  });

  // ============================================================================
  // SUSPENSE PHASE: Breathing animation (theatrical mode only)
  // Oscillates scale between breathing.min and breathing.max during suspense
  // ============================================================================
  useEffect(() => {
    if (!isInSuspense || !effectiveConfig.suspense.enabled) return;

    const { min, max, duration } = effectiveConfig.suspense.breathing;
    let animationFrameId: number;
    let startTime = performance.now();
    let direction = 1; // 1 = expanding, -1 = contracting

    const animateBreathing = (currentTime: number) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);

      // Ease in-out for smooth breathing
      const eased = 0.5 - 0.5 * Math.cos(progress * Math.PI);
      const scale = direction === 1
        ? min + (max - min) * eased
        : max - (max - min) * eased;

      setSuspenseScale(scale);

      if (progress >= 1) {
        // Reverse direction for next cycle
        direction *= -1;
        startTime = currentTime;
      }

      animationFrameId = requestAnimationFrame(animateBreathing);
    };

    animationFrameId = requestAnimationFrame(animateBreathing);

    return () => {
      if (animationFrameId) cancelAnimationFrame(animationFrameId);
    };
  }, [isInSuspense, effectiveConfig.suspense]);

  // ============================================================================
  // SUSPENSE PHASE: Message rotation (theatrical mode only)
  // Cycles through suspense messages at configured interval
  // ============================================================================
  useEffect(() => {
    if (!isInSuspense || !effectiveConfig.suspense.enabled) return;

    const interval = setInterval(() => {
      setSuspenseMessageIndex((prev) => (prev + 1) % SUSPENSE_MESSAGES.length);
    }, effectiveConfig.suspense.messageRotationInterval);

    return () => clearInterval(interval);
  }, [isInSuspense, effectiveConfig.suspense, SUSPENSE_MESSAGES.length]);

  // ============================================================================
  // SUSPENSE PHASE: Slow crawl to 99% + API response detection
  // When API responds during suspense, complete the animation
  // ============================================================================
  useEffect(() => {
    if (!isInSuspense) return;

    // Start slow crawl from 92% to 99%
    const crawlDuration = effectiveConfig.suspense.crawlDuration;
    let crawlAnimationId: number;
    const crawlStartTime = performance.now();
    const startProgress = convergenceEnd; // 0.92
    const endProgress = 0.99;

    const animateCrawl = (currentTime: number) => {
      const elapsed = currentTime - crawlStartTime;
      const crawlProgress = Math.min(elapsed / crawlDuration, 1);
      const newProgress = startProgress + (endProgress - startProgress) * crawlProgress;
      setProgress(newProgress);

      if (crawlProgress < 1 && !analysisResultRef.current) {
        crawlAnimationId = requestAnimationFrame(animateCrawl);
      }
      // If API is ready, the analysisResult useEffect will handle completion
    };

    crawlAnimationId = requestAnimationFrame(animateCrawl);

    return () => {
      if (crawlAnimationId) cancelAnimationFrame(crawlAnimationId);
    };
  }, [isInSuspense, effectiveConfig.suspense.crawlDuration, convergenceEnd]);

  // ============================================================================
  // SUSPENSE PHASE: Exit when API response arrives
  // Completes animation and reveals badge when analysisResult becomes available
  // ============================================================================
  useEffect(() => {
    if (!isInSuspense || !analysisResult) return;

    // API response received during suspense - complete the animation
    setIsInSuspense(false);
    setProgress(1);
    setPhase('complete');
    phaseRef.current = 'complete';

    // 1. DIM TEXT: Fade text container to backdrop
    Animated.timing(textOpacity, {
      toValue: REVELATION_CONSTANTS.TEXT_BACKDROP_OPACITY,
      duration: REVELATION_CONSTANTS.TEXT_DIM_DURATION_MS,
      useNativeDriver: true,
    }).start();

    // 2. BADGE REVEAL: Spring in the badge with scale multiplier
    const scaleMultiplier = effectiveConfig.badgeReveal?.scaleMultiplier || 1;
    setTimeout(() => {
      Animated.parallel([
        Animated.spring(badgeScale, {
          toValue: scaleMultiplier,
          tension: 300,
          friction: 20,
          useNativeDriver: true,
        }),
        Animated.timing(badgeOpacity, {
          toValue: 1,
          duration: 200,
          useNativeDriver: true,
        }),
      ]).start(() => {
        setShowContinueButton(true);
      });
    }, REVELATION_CONSTANTS.BADGE_DELAY_MS);
  }, [isInSuspense, analysisResult, textOpacity, badgeScale, badgeOpacity, effectiveConfig.badgeReveal]);

  // Main animation loop
  useEffect(() => {
    const prefersReducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    if (prefersReducedMotion) {
      setCurrentZoomText(words.slice(0, targetWordCount).join(' '));
      setCurrentScale(1);
      setProgress(1);
      setShowContinueButton(true);
      return;
    }

    startTimeRef.current = performance.now();
    setPhase('emergence');
    phaseRef.current = 'emergence';

    const animate = (currentTime: number) => {
      if (skipRequestedRef.current) return;

      const elapsed = currentTime - startTimeRef.current;
      const newProgress = Math.min(elapsed / totalDuration, 1);
      setProgress(newProgress);

      const emergenceEnd = LETTER_ZOOM_PHASE_TIMING.emergence;
      const transitionEnd = emergenceEnd + LETTER_ZOOM_PHASE_TIMING.transition;
      const readingEnd = transitionEnd + LETTER_ZOOM_PHASE_TIMING.reading;
      const convergenceEnd = readingEnd + LETTER_ZOOM_PHASE_TIMING.convergence;

      if (newProgress < emergenceEnd) {
        // EMERGENCE PHASE
        if (phaseRef.current !== 'emergence') {
          setPhase('emergence');
          phaseRef.current = 'emergence';
        }

        const emergenceProgress = newProgress / emergenceEnd;

        // Narrator subtitle logic:
        // 1. During emergence window (25%-90%): Show fallback quips if LLM not arrived yet
        // 2. When LLM narrator arrives: Show immediately and keep showing for display duration
        // 3. Hide after display duration elapses (for both fallback quips and LLM narrator)
        const { EMERGENCE_SHOW_START, EMERGENCE_SHOW_END, MAX_QUIP_LENGTH } = NARRATOR_SUBTITLE_CONSTANTS;

        // Check if LLM narrator just arrived and should extend display
        const llmNarratorDisplaying = llmNarratorArrivedRef.current &&
          (Date.now() - llmNarratorArrivalTimeRef.current) < LLM_NARRATOR_DISPLAY_DURATION_MS;

        if (emergenceProgress >= EMERGENCE_SHOW_START && emergenceProgress <= EMERGENCE_SHOW_END) {
          // In emergence window - show fallback quip if LLM hasn't arrived yet
          if (!narratorSubtitleShownRef.current && !llmNarratorArrivedRef.current) {
            // Use pattern-matched quips as fallback
            let quip = getPatternQuip(
              [], // TODO: Pass analysisResult.patterns when available
              text.substring(0, 50),
              undefined,
              []
            );
            let attempts = 0;
            while (quip.length > MAX_QUIP_LENGTH && attempts < 5) {
              quip = getPatternQuip([], text.substring(0, 50 + attempts * 10));
              attempts++;
            }
            if (quip.length > MAX_QUIP_LENGTH) {
              quip = quip.substring(0, MAX_QUIP_LENGTH - 3) + '...';
            }

            setNarratorQuip(quip);
            setShowNarratorSubtitle(true);
            narratorSubtitleShownRef.current = true;
          }
        } else if (emergenceProgress > EMERGENCE_SHOW_END && showNarratorSubtitle && !llmNarratorDisplaying) {
          // Past emergence window AND not showing LLM narrator - hide
          setShowNarratorSubtitle(false);
        }

        // Hide LLM narrator after its display duration elapses
        if (llmNarratorArrivedRef.current && !llmNarratorDisplaying && showNarratorSubtitle) {
          setShowNarratorSubtitle(false);
        }

        let currentStepIndex = 0;
        for (let i = 0; i < dynamicStepBoundaries.length; i++) {
          const [, end] = dynamicStepBoundaries[i];
          if (emergenceProgress <= end) {
            currentStepIndex = i;
            break;
          }
          currentStepIndex = i;
        }

        const [start, end] = dynamicStepBoundaries[currentStepIndex];
        const stepDuration = end - start;
        const stepFraction = stepDuration > 0
          ? Math.min((emergenceProgress - start) / stepDuration, 1)
          : 1;

        const currentStep = dynamicZoomSteps[currentStepIndex];
        const nextStep = dynamicZoomSteps[Math.min(currentStepIndex + 1, dynamicZoomSteps.length - 1)];

        const eased = 1 - Math.pow(1 - stepFraction, 3);
        const interpolatedScale = currentStep.scale - (currentStep.scale - nextStep.scale) * eased;

        let zoomText: string;
        if (currentStep.lineCount && readingLinesRef.current.length > 0) {
          const actualLines = readingLinesRef.current.slice(
            PADDING_LINES,
            PADDING_LINES + currentStep.lineCount
          );
          zoomText = actualLines.join('\n');
        } else {
          const wordCount = currentStep.wordCount;
          zoomText = wordCount === 1
            ? (words[0]?.charAt(0) || '')
            : words.slice(0, wordCount).join(' ');
        }

        setCurrentZoomText(zoomText);
        setCurrentScale(interpolatedScale);
        setCurrentGlowColor(getZoomGlowColor(interpolatedScale));

      } else if (newProgress < transitionEnd) {
        // TRANSITION PHASE
        if (phaseRef.current !== 'transition') {
          setPhase('transition');
          phaseRef.current = 'transition';
          // Hide narrator subtitle when leaving emergence phase (unless LLM narrator still displaying)
          const llmNarratorDisplaying = llmNarratorArrivedRef.current &&
            (Date.now() - llmNarratorArrivalTimeRef.current) < LLM_NARRATOR_DISPLAY_DURATION_MS;
          if (!llmNarratorDisplaying) {
            setShowNarratorSubtitle(false);
          }
          const lastStep = dynamicZoomSteps[dynamicZoomSteps.length - 1];
          if (lastStep.lineCount && readingLinesRef.current.length > 0) {
            const finalLines = readingLinesRef.current.slice(
              PADDING_LINES,
              PADDING_LINES + lastStep.lineCount
            );
            setCurrentZoomText(finalLines.join('\n'));
          } else {
            setCurrentZoomText(words.slice(0, targetWordCount).join(' '));
          }
          setCurrentScale(1);
        }

        const transProgress = (newProgress - emergenceEnd) / LETTER_ZOOM_PHASE_TIMING.transition;
        // Use setValue() directly — Animated.timing() per frame creates 60+ queued animations/sec
        zoomTextOpacity.setValue(1 - transProgress * 0.7);

      } else if (newProgress < readingEnd) {
        // READING PHASE
        if (phaseRef.current !== 'reading') {
          setPhase('reading');
          phaseRef.current = 'reading';
        }

        const readingProgress = (newProgress - transitionEnd) / LETTER_ZOOM_PHASE_TIMING.reading;
        const totalLines = readingLinesRef.current.length;
        const actualLineCount = totalLines - (PADDING_LINES * 2);

        // Window optimization: cap at 12 lines starting at 35% position
        const [windowStart, windowEnd] = calculateReadingWindow(actualLineCount);
        const windowSize = windowEnd - windowStart + 1;

        // Calculate target line within window
        const lineProgress = readingProgress * windowSize;
        const targetLineInWindow = Math.min(Math.floor(lineProgress), windowSize - 1);
        const targetLine = windowStart + targetLineInWindow;

        // Apply padding offset
        const centerOffset = (targetLine + PADDING_LINES) * LINE_HEIGHT + (LINE_HEIGHT / 2);

        setReadingScrollY(-centerOffset);
        setCurrentLineIndex(targetLine + PADDING_LINES);

      } else if (newProgress < convergenceEnd) {
        // CONVERGENCE PHASE (85-93%) - Shrink, center, clear text
        // Reference: .superdesign/design_iterations/book_scanner_6_letter_zoom.html lines 929-949
        if (phaseRef.current !== 'convergence') {
          setPhase('convergence');
          phaseRef.current = 'convergence';
        }

        const convProgress = (newProgress - readingEnd) / LETTER_ZOOM_PHASE_TIMING.convergence;
        // easeOutQuad for smooth deceleration (matching original HTML)
        const eased = 1 - Math.pow(1 - convProgress, 2);

        // 1. SHRINK: Scale from 1.0 → 0.65 (shows more content vertically)
        const newScale = 1 - (eased * CONVERGENCE_CONSTANTS.SCALE_REDUCTION);
        setConvergenceScale(newScale);

        // 2. CENTER: Move to middle line position
        const totalLines = readingLinesRef.current.length;
        const actualLineCount = totalLines - (PADDING_LINES * 2);
        const centerLine = Math.floor(actualLineCount / 2);
        const centerOffset = (centerLine + PADDING_LINES) * LINE_HEIGHT + (LINE_HEIGHT / 2);
        setReadingScrollY(-centerOffset);

        // 3. TEXT CLARITY: Reduce blur + increase opacity (text becomes MORE visible)
        // Blur: reduces from current → 0 (web only, applied per-line in render)
        setConvergenceBlurProgress(eased);

        // Opacity: increases from START_OPACITY → END_OPACITY
        // Use setValue() directly — Animated.timing() per frame creates 60+ queued animations/sec
        const opacityRange = CONVERGENCE_CONSTANTS.END_OPACITY - CONVERGENCE_CONSTANTS.START_OPACITY;
        textOpacity.setValue(CONVERGENCE_CONSTANTS.START_OPACITY + (eased * opacityRange));

      } else if (newProgress >= convergenceEnd && !isInSuspense && !suspenseStartedRef.current) {
        // CHECK FOR SUSPENSE PHASE AT 92% (convergenceEnd)
        // Theatrical mode: Enter suspense if enabled AND API not ready
        // Quick mode: Skip straight to revelation

        if (effectiveConfig.suspense.enabled && !analysisResultRef.current) {
          // ENTER SUSPENSE PHASE - Wait for API with breathing animation
          suspenseStartedRef.current = true;
          suspenseStartTimeRef.current = performance.now();
          setIsInSuspense(true);
          setPhase('convergence'); // Stay in convergence visually
          phaseRef.current = 'convergence';

          // Don't continue rAF loop - suspense useEffect will handle crawl
          return;
        }

        // Quick mode OR API already ready: Go straight to revelation
        if (phaseRef.current !== 'complete') {
          setPhase('complete');
          phaseRef.current = 'complete';

          // 1. DIM TEXT: Fade text container to 12% opacity (becomes backdrop)
          Animated.timing(textOpacity, {
            toValue: REVELATION_CONSTANTS.TEXT_BACKDROP_OPACITY,
            duration: REVELATION_CONSTANTS.TEXT_DIM_DURATION_MS,
            useNativeDriver: true,
          }).start();

          // 2. BADGE REVEAL: After delay, spring in the badge
          if (analysisResultRef.current) {
            const scaleMultiplier = effectiveConfig.badgeReveal?.scaleMultiplier || 1;
            setTimeout(() => {
              Animated.parallel([
                Animated.spring(badgeScale, {
                  toValue: scaleMultiplier, // Apply theatrical scale multiplier
                  tension: 300,
                  friction: 20,
                  useNativeDriver: true,
                }),
                Animated.timing(badgeOpacity, {
                  toValue: 1,
                  duration: 200,
                  useNativeDriver: true,
                }),
              ]).start();
            }, REVELATION_CONSTANTS.BADGE_DELAY_MS);
          }
        }
      } else if (isInSuspense) {
        // SUSPENSE PHASE: Handled by separate useEffect, skip rAF updates
        return;
      }

      if (newProgress < 1) {
        rafIdRef.current = requestAnimationFrame(animate);
      } else {
        setShowContinueButton(true);
      }
    };

    rafIdRef.current = requestAnimationFrame(animate);
    // Skip hint timeout moved to analysisResult useEffect - shows only after API response

    return () => {
      if (rafIdRef.current) cancelAnimationFrame(rafIdRef.current);

      // Memory leak fix: Stop all Animated.Value animations on unmount
      // Prevents listeners from persisting after component cleanup
      badgeScale.stopAnimation();
      badgeOpacity.stopAnimation();
      textOpacity.stopAnimation();
      zoomTextOpacity.stopAnimation();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [totalDuration, words]);

  // Handle skip - jumps to badge reveal (revelation phase), NOT directly to full analysis
  // User requirement: Skip should show badge reveal animation, THEN show continue button
  const handleSkip = useCallback(() => {
    skipRequestedRef.current = true;
    if (rafIdRef.current) cancelAnimationFrame(rafIdRef.current);

    // Exit suspense phase if currently in it
    if (isInSuspense) {
      setIsInSuspense(false);
    }

    // Jump to revelation phase (not complete) - matches convergence end state
    setCurrentZoomText(words.slice(0, targetWordCount).join(' '));
    setCurrentScale(CONVERGENCE_CONSTANTS.END_SCALE); // 0.75 - converged scale
    setConvergenceScale(CONVERGENCE_CONSTANTS.END_SCALE); // Match convergence final state
    setConvergenceBlurProgress(1); // Blur fully reduced (text is clear)
    setProgress(0.92); // Revelation phase start boundary
    setPhase('revelation');
    phaseRef.current = 'revelation';
    // Hide narrator subtitle if visible (unless LLM narrator still displaying)
    const llmNarratorDisplaying = llmNarratorArrivedRef.current &&
      (Date.now() - llmNarratorArrivalTimeRef.current) < LLM_NARRATOR_DISPLAY_DURATION_MS;
    if (!llmNarratorDisplaying) {
      setShowNarratorSubtitle(false);
    }

    // Dim text to backdrop (revelation phase visual)
    Animated.timing(textOpacity, {
      toValue: REVELATION_CONSTANTS.TEXT_BACKDROP_OPACITY, // 0.5
      duration: REVELATION_CONSTANTS.TEXT_DIM_DURATION_MS, // 150ms
      useNativeDriver: true,
    }).start();

    if (analysisResultRef.current) {
      // Badge reveal animation with delay (matches natural revelation flow)
      const scaleMultiplier = effectiveConfig.badgeReveal?.scaleMultiplier || 1;
      setTimeout(() => {
        Animated.parallel([
          Animated.spring(badgeScale, {
            toValue: scaleMultiplier, // Apply theatrical scale multiplier
            tension: 300,
            friction: 20,
            useNativeDriver: true,
          }),
          Animated.timing(badgeOpacity, {
            toValue: 1,
            duration: 200,
            useNativeDriver: true,
          }),
        ]).start(() => {
          // AFTER badge animation completes, show continue button
          setPhase('complete');
          phaseRef.current = 'complete';
          setShowContinueButton(true);
        });
      }, REVELATION_CONSTANTS.BADGE_DELAY_MS); // 80ms delay
    } else {
      // Edge case: No analysis result - go straight to complete
      setPhase('complete');
      phaseRef.current = 'complete';
      setShowContinueButton(true);
    }

    // NOTE: Do NOT call onSkip() here - we want to show badge reveal first
    // User will click "View Full Analysis" button which calls onComplete()
  }, [badgeScale, badgeOpacity, words, targetWordCount, textOpacity, isInSuspense, effectiveConfig.badgeReveal]);

  // Handle continue
  const handleContinue = useCallback(() => {
    onComplete?.();
  }, [onComplete]);

  // Get focus level for reading lines
  const getFocusLevel = (lineIdx: number): 0 | 1 | 2 | 3 | 4 => {
    const distance = Math.abs(lineIdx - currentLineIndex);
    return Math.min(distance, 4) as 0 | 1 | 2 | 3 | 4;
  };

  const glassOpacity = phase === 'reading' ? 1 : 0;

  return (
    <Pressable
      onPress={showContinueButton ? undefined : handleSkip}
      // Keep pointerEvents='auto' to prevent clicks from falling through to underlying page elements
      // Child button will still receive clicks because React Native handles child events first
      style={[styles.container, { pointerEvents: 'auto' as const }, style]}
      accessibilityLabel={t('home.modern.skip_animation')}
    >
      <LinearGradient
        colors={ANIMATION_BG_GRADIENT}
        style={styles.gradient}
      >
        {/* TOP-LOADER: Y2K Progress Bar */}
        <View style={styles.topLoaderContainer}>
          <LinearGradient
            colors={[Y2K_GRADIENT_COLORS.pink, Y2K_GRADIENT_COLORS.lavender, Y2K_GRADIENT_COLORS.mint]}
            start={{ x: 0, y: 0 }}
            end={{ x: 1, y: 0 }}
            style={[styles.topLoaderBar, { width: `${progress * 100}%` }]}
          />
        </View>

        <View style={styles.content}>
          {/* EMERGENCE: Letter Zoom */}
          {(phase === 'emergence' || phase === 'transition') && (
            <Animated.View
              style={[styles.letterZoomContainer, { opacity: zoomTextOpacity }]}
            >
              <Text
                style={[
                  styles.letterZoomText,
                  {
                    transform: [{ scale: currentScale }],
                    // Web: CSS textShadow (dynamic glow based on scale)
                    textShadow: `0px 0px ${Math.min(24, currentScale * 2)}px ${currentGlowColor}`,
                  } as any,
                ]}
              >
                {currentZoomText}
              </Text>
              {/* Chromatic aberration layers */}
              <Text
                style={[
                  styles.letterZoomText,
                  styles.letterZoomChromaticR,
                  {
                    transform: [
                      { scale: currentScale },
                      { translateX: -Math.min(3, currentScale * 0.4) },
                    ],
                  },
                ]}
              >
                {currentZoomText}
              </Text>
              <Text
                style={[
                  styles.letterZoomText,
                  styles.letterZoomChromaticB,
                  {
                    transform: [
                      { scale: currentScale },
                      { translateX: Math.min(3, currentScale * 0.4) },
                    ],
                  },
                ]}
              >
                {currentZoomText}
              </Text>
            </Animated.View>
          )}

          {/* NARRATOR SUBTITLE: Reality TV commentary during emergence */}
          {/* Disabled via NARRATOR_EXPERIENCE_ENABLED flag - code preserved for future use */}
          {NARRATOR_EXPERIENCE_ENABLED && showNarratorSubtitle && narratorQuip && (
            <View
              style={[
                styles.narratorSubtitleContainer,
                {
                  // Web-specific backdrop blur for glass morphism effect
                  ...(Platform.OS === 'web' ? {
                    backdropFilter: 'blur(8px)',
                  } as any : {}),
                },
              ]}
            >
              <View style={styles.narratorSubtitle}>
                <Text style={styles.narratorSubtitleText}>
                  {tContent(narratorQuip as any)}
                </Text>
              </View>
            </View>
          )}

          {/* READING: Glass overlays + scrolling text */}
          {(phase === 'reading' || phase === 'convergence' || phase === 'complete') && (
            <>
              <LinearGradient
                pointerEvents="none"
                colors={['rgba(10, 0, 18, 0.95)', 'rgba(10, 0, 18, 0)']}
                style={[styles.glassOverlayTop, { opacity: glassOpacity }]}
              />
              <LinearGradient
                pointerEvents="none"
                colors={['rgba(10, 0, 18, 0)', 'rgba(10, 0, 18, 0.95)']}
                style={[styles.glassOverlayBottom, { opacity: glassOpacity }]}
              />
              <LinearGradient
                pointerEvents="none"
                colors={[Y2K_GRADIENT_COLORS.pink + '10', Y2K_GRADIENT_COLORS.lavender + '20', Y2K_GRADIENT_COLORS.mint + '10']}
                start={{ x: 0, y: 0 }}
                end={{ x: 1, y: 0 }}
                style={[styles.focusGlowZone, { opacity: glassOpacity }]}
              />

              <Animated.View
                pointerEvents={phase === 'complete' ? 'none' : 'auto'}
                style={[
                  styles.readingContainer,
                  {
                    opacity: textOpacity,
                    transform: [
                      { translateY: readingScrollY },
                      { scale: convergenceScale },  // Convergence: shrinks from 1.0 → 0.65
                    ],
                  },
                ]}
              >
                {readingLines.map((line, idx) => {
                  const isPaddingLine = idx < PADDING_LINES || idx >= readingLines.length - PADDING_LINES;
                  const focusLevel = getFocusLevel(idx);
                  const focusStyle = FOCUS_BLUR_LEVELS[focusLevel];
                  // During convergence, blur reduces from current value → 0
                  const effectiveBlur = focusStyle.blur * (1 - convergenceBlurProgress);
                  return (
                    <Text
                      key={`line-${idx}`}
                      style={[
                        styles.readingLine,
                        {
                          height: LINE_HEIGHT,
                          opacity: isPaddingLine ? focusStyle.opacity * 0.3 : focusStyle.opacity,
                          transform: [{ scale: focusStyle.scale }],
                          ...(Platform.OS === 'web' ? {
                            filter: `blur(${effectiveBlur}px)`,
                          } : {}),
                        },
                        focusLevel === 0 && !isPaddingLine && styles.readingLineFocused,
                      ]}
                    >
                      {line}
                    </Text>
                  );
                })}
              </Animated.View>
            </>
          )}

          {/* SUSPENSE PHASE: Breathing indicator with rotating messages (theatrical mode) */}
          {isInSuspense && (
            <View
              style={[
                styles.suspenseContainer,
                { transform: [{ scale: suspenseScale }] },
              ]}
            >
              <ActivityIndicator
                size="large"
                color={Y2K_GRADIENT_COLORS.lavender}
                style={styles.suspenseSpinner}
              />
              <Text style={styles.suspenseMessage}>
                {SUSPENSE_MESSAGES[suspenseMessageIndex]}
              </Text>
            </View>
          )}

          {/* Drama badge (revelation phase) */}
          {analysisResult && !isInSuspense && (
            <Animated.View
              style={[
                styles.badgeContainer,
                {
                  opacity: badgeOpacity,
                  transform: [
                    {
                      scale: badgeScale.interpolate({
                        inputRange: [0, 0.5, 1],
                        outputRange: [0, 1.2, 1],
                      }),
                    },
                  ],
                },
              ]}
            >
              <LinearGradient colors={categoryStyle.gradient} style={styles.badge}>
                <Text style={styles.scoreText}>{formatDramaScore(analysisResult.score)}</Text>
                <Text style={styles.categoryLabel}>{categoryStyle.label}</Text>
              </LinearGradient>
            </Animated.View>
          )}

          {/* Skip hint */}
          {showSkipHint && !showContinueButton && (
            <Text style={styles.skipHint as any}>{t('home.modern.tap_to_skip')}</Text>
          )}

          {/* Continue button with Y2K traveling spark border */}
          {showContinueButton && (
            <View style={styles.continueButtonWrapper}>
              {/* Animated traveling spark border (web only) */}
              {!prefersReducedMotion && Platform.OS === 'web' && (
                <Animated.View
                  style={[
                    styles.rotatingBorderContainer,
                    { transform: [{ rotate: borderRotate }] },
                  ]}
                >
                  <View
                    style={[
                      styles.rotatingBorderGradient,
                      {
                        background: `conic-gradient(from 0deg, transparent 0deg, ${Y2K_GRADIENT_COLORS.pink} 15deg, ${Y2K_GRADIENT_COLORS.lavender} 45deg, ${Y2K_GRADIENT_COLORS.mint} 75deg, ${Y2K_GRADIENT_COLORS.mint}88 90deg, transparent 105deg, transparent 360deg)`,
                      } as any,
                    ]}
                  />
                </Animated.View>
              )}
              {/* Static gradient for reduced motion (WCAG 2.1) */}
              {(prefersReducedMotion || Platform.OS !== 'web') && (
                <LinearGradient
                  colors={[Y2K_GRADIENT_COLORS.pink, Y2K_GRADIENT_COLORS.lavender, Y2K_GRADIENT_COLORS.mint]}
                  start={{ x: 0, y: 0 }}
                  end={{ x: 1, y: 0 }}
                  style={styles.staticBorderGradient}
                />
              )}
              <Pressable
                onPress={handleContinue}
                style={styles.continueButton as any}
                accessibilityRole="button"
                accessibilityLabel={t('home.modern.view_full_analysis', { defaultValue: 'View Full Analysis' })}
              >
                <LinearGradient
                  colors={[Y2K_GRADIENT_COLORS.lavender + '40', Y2K_GRADIENT_COLORS.mint + '30']}
                  start={{ x: 0, y: 0 }}
                  end={{ x: 1, y: 0 }}
                  style={styles.continueButtonGradient}
                >
                  <Text style={styles.continueButtonText}>{t('home.modern.view_full_analysis', { defaultValue: 'View Full Analysis' })}</Text>
                </LinearGradient>
              </Pressable>
            </View>
          )}
        </View>

        {/* Glitch Text Progress Indicator */}
        <View style={styles.glitchProgressContainer} pointerEvents="none">
          <Text style={styles.glitchProgressText}>
            {phase === 'complete' ? 'COMPLETE' : isInSuspense ? `PROCESSING... ${Math.round(progress * 100)}%` : `ANALYZING... ${Math.round(progress * 100)}%`}
          </Text>
          <Text style={[styles.glitchProgressText, styles.glitchLayerR]}>
            {phase === 'complete' ? 'COMPLETE' : isInSuspense ? `PROCESSING... ${Math.round(progress * 100)}%` : `ANALYZING... ${Math.round(progress * 100)}%`}
          </Text>
          <Text style={[styles.glitchProgressText, styles.glitchLayerB]}>
            {phase === 'complete' ? 'COMPLETE' : isInSuspense ? `PROCESSING... ${Math.round(progress * 100)}%` : `ANALYZING... ${Math.round(progress * 100)}%`}
          </Text>
        </View>
      </LinearGradient>
    </Pressable>
  );
});

export default LetterZoomAnimation;
